import { Component, Input, ViewChild } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { EmpresaModalComponent } from '../empresa-modal/empresa-modal.component';
import { PersonaComponent } from '../persona/persona.component';
import { ApiService } from '../services/api.service';
import { LoadingService } from '../services/loading.service';
import { ToastService } from '../services/toast.service';
import { VendedorModalComponent } from '../vendedor-modal/vendedor-modal.component';

declare var bootstrap: any;

@Component({
  selector: 'app-persona-modal',
  templateUrl: './persona-modal.component.html',
  styleUrl: './persona-modal.component.css',
})
export class PersonaModalComponent {
  @Input() sexos: any[] = [];
  @Input() estadosCiviles: any[] = [];
  @Input() nacionalidades: any[] = [];
  @Input() parentescos: any[] = [];
  @Input() tiposBeneficiarios: any[] = [];
  @Input() planes: any[] = [];
  @Input() obrasSociales: any[] = [];
  @Input() revistas: any[] = [];
  @Input() provincias: any[] = [];
  @Input() tipoModal: 'venta' | 'persona' = 'venta';
  @ViewChild(VendedorModalComponent) vendedorModal!: VendedorModalComponent;
  @ViewChild(EmpresaModalComponent) empresaModal!: EmpresaModalComponent;

  vendedores: any[] = [];
  modalPersonaVisible: boolean = false;
  nuevaPersona: any = this.getInitialPersona();
  localidades: any[] = [];
  errores: { [key: string]: string } = {};
  seccionActual:
    | 'datosPersonales'
    | 'datosAfiliacion'
    | 'nuevaPersonaAdherente' = 'datosPersonales';
  maxDate!: NgbDateStruct;
  minDate!: NgbDateStruct;
  tieneTitular: boolean = false;
  cuilTitularPrevio: string = '';
  idPlanPrevio: string = '';
  idObraSocialPrevio: string = '';
  idRevistaPrevio: string = '';
  idTipoBeneficiarioPrevio: string = '';
  idVendedorPrevio: string = '';
  cuitEmpresaPrevio: string = '';
  planFijoPrevio: boolean = false;
  aportePrevio: number = 0.0;
  personaSeleccionada: any = null;
  cuilTitularPrevioVerificado: string = '';
  telefonoPrevio: string = '';
  domicilioPrevio: string = '';
  numeroDomicilioPrevio: string = '';
  pisoPrevio: string = '';
  departamentoPrevio: string = '';
  provinciaPrevio: string = '';
  idLocalidadPrevio: string = '';
  idNacionalidadPrevio: string = '';
  fechaVentaPrevio: string = '';

  // Propiedades para el manejo de empresas
  empresas: any[] = [];
  empresaFiltro: string = '';
  empresaFiltrada: any[] = [];
  empresaSeleccionada: any = null;
  empresaModalSuscrito: boolean = false;

  private readonly SOLO_LETRAS = /^[a-zA-ZÀ-ÿ\s']+$/;
  private readonly SOLO_NUMEROS = /^[0-9]+$/;
  private readonly CUIL_VALIDO = /^[0-9]{11}$/;
  private readonly DNI_VALIDO = /^[0-9]{8}$/;
  private readonly TELEFONO_VALIDO = /^[0-9]{8,}$/;
  private destroy$ = new Subject<void>();

  constructor(
    private apiSrv: ApiService,
    private personaComponent: PersonaComponent,
    private toastSrv: ToastService,
    private loadingSrv: LoadingService
  ) {}
  ngOnInit(): void {
    const dateLimits = this.personaComponent.setDateLimits();
    // Asigna los límites de fecha al componente actual
    this.minDate = dateLimits.minDate;
    this.maxDate = dateLimits.maxDate;

    this.cargarVendedoresActivos();
    this.cargarEmpresas();

    // Suscribirse UNA VEZ a los eventos del vendedorModal cuando esté disponible
    // (se ejecutará después de ViewInit)
    setTimeout(() => {
      if (this.vendedorModal) {
        this.vendedorModal.vendedorGuardado
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.cargarVendedoresActivos();
            if (this.modalPersonaVisible) {
              setTimeout(() => {
                this.showModal('nuevoPersonaModal');
                this.modalPersonaVisible = false;
              }, 500);
            }
          });

        // Añadir esta nueva suscripción
        this.vendedorModal.vendedorModalCancelado
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            if (this.modalPersonaVisible) {
              setTimeout(() => {
                this.showModal('nuevoPersonaModal');
                this.modalPersonaVisible = false;
              }, 300);
            }
          });
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargarVendedoresActivos(): void {
    this.apiSrv.obtenerVendedoresActivos().subscribe({
      next: (data) => {
        this.vendedores = data;
      },
      error: (error) => {
        this.toastSrv.mostrarNotificacion(
          'Error al cargar vendedores activos',
          'error'
        );
      },
    });
  }

  // Carga la lista de empresas del servidor
  cargarEmpresas(): void {
    this.apiSrv.obtenerEmpresas().subscribe((data: any[]) => {
      this.empresas = (data || []).sort((a, b) =>
        a.razonSocial.localeCompare(b.razonSocial)
      );
      this.empresaFiltrada = [];
    });
  }

  filtrarEmpresas(): void {
    const filtro = (this.empresaFiltro || '').toLowerCase();

    if (filtro.length < 3) {
      this.empresaFiltrada = [];
      return;
    }

    this.empresaFiltrada = this.empresas
      .filter((e) => {
        const coincideRazonSocial = e.razonSocial
          .toLowerCase()
          .includes(filtro);
        const coincideCuit = (e.cuit + '').toLowerCase().includes(filtro);
        return coincideRazonSocial || coincideCuit;
      })
      .sort((a, b) => a.razonSocial.localeCompare(b.razonSocial));
  }

  onEmpresaSeleccionada(cuit: any): void {
    const cuitStr = cuit.toString();

    this.empresaSeleccionada = this.empresas.find(
      (e) => e.cuit.toString() === cuitStr
    );

    if (this.empresaSeleccionada) {
      this.nuevaPersona.cuitEmpresa = cuitStr;
    }
  }

  agregarPersona(): void {
    const validarCampos =
      this.seccionActual === 'nuevaPersonaAdherente'
        ? this.validarPersonaPrimerCampo()
        : Promise.resolve(true);

    validarCampos.then((valido) => {
      if (valido && this.validarPersonaSegundoCampo()) {
        this.loadingSrv.setLoading(true);
        this.apiSrv.agregarPersona(this.nuevaPersona).subscribe({
          next: this.onPersonaGuardada.bind(this),
          error: this.onGuardarError.bind(this),
        });
      }
    });
  }

  private onGuardarError(error: any): void {
    this.loadingSrv.setLoading(false);
    this.toastSrv.mostrarNotificacion(
      'Error al guardar la persona.<br>' +
        (error?.error ? `${error.error}` : ''),
      'error'
    );
  }

  onProvinciaChange(event: any): void {
    const provinciaId = event.target.value;
    this.apiSrv.obtenerLocalidadesSegunIdProvincia(provinciaId).subscribe({
      next: (localidades) => (this.localidades = localidades),
      error: () =>
        this.toastSrv.mostrarNotificacion(
          'Error al cargar localidades.',
          'error'
        ),
    });
  }

  siguiente(): void {
    this.validarPersonaPrimerCampo().then((valido) => {
      if (valido) {
        if (
          this.cuilTitularPrevioVerificado !== this.nuevaPersona.cuilTitular
        ) {
          this.reiniciarDatosAfiliacion();
        }
        this.seccionActual = 'datosAfiliacion';

        if (this.nuevaPersona.cuil !== this.nuevaPersona.cuilTitular) {
          this.verificarCuilTitular().then((resultado) => {
            if (!resultado) {
              this.toastSrv.mostrarNotificacion(
                'El CUIL del titular ingresado no existe o no se pudo verificar.',
                'warning'
              );
            }
          });
        }
      }
    });
  }

  anterior(): void {
    this.seccionActual = 'datosPersonales';
  }

  private validarPersonaPrimerCampo(): Promise<boolean> {
    return new Promise((resolve) => {
      this.errores = {};
      let valido = true;

      valido =
        this.validarCampo(
          this.nuevaPersona.nombre,
          'nombre',
          this.SOLO_LETRAS,
          'El nombre es obligatorio.',
          'El nombre debe contener solo letras.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.apellido,
          'apellido',
          this.SOLO_LETRAS,
          'El apellido es obligatorio.',
          'El apellido debe contener solo letras.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.documento,
          'documento',
          this.DNI_VALIDO,
          'El documento es obligatorio.',
          'El documento debe contener solo números y solo 8 dígitos.'
        ) && valido;

      // Validación especial para fecha de nacimiento
      const resultadoFecha = this.validarFechaCompleta(
        this.nuevaPersona.fechaNacimiento
      );
      if (!resultadoFecha.valido) {
        this.errores['fechaNacimiento'] =
          resultadoFecha.mensaje || 'Fecha de nacimiento inválida.';
        valido = false;
      }

      valido =
        this.validarCampo(
          this.nuevaPersona.telefono,
          'telefono',
          this.TELEFONO_VALIDO,
          'El telefono es obligatorio.',
          'El teléfono debe contener solo números y tener como mínimo 8 dígitos.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.cuil,
          'cuil',
          this.CUIL_VALIDO,
          'El cuil es obligatorio.',
          'El CUIL debe tener exactamente 11 dígitos numéricos.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.cuilTitular,
          'cuilTitular',
          this.CUIL_VALIDO,
          'El cuil del titular es obligatorio.',
          'El CUIL del titular debe tener exactamente 11 dígitos numéricos.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.domicilio,
          'domicilio',
          null,
          'El domicilio es obligatorio.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.numeroDomicilio,
          'numeroDomicilio',
          null,
          'El numero de domicilio es obligatorio.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.idLocalidad,
          'idLocalidad',
          null,
          'La localidad es obligatoria.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.idSexo,
          'idSexo',
          null,
          'El sexo es obligatorio.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.idEstadoCivil,
          'idEstadoCivil',
          null,
          'El estado civil es obligatorio.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.idNacionalidad,
          'idNacionalidad',
          null,
          'La nacionalidad es obligatoria.'
        ) && valido;
      valido =
        this.validarCampo(
          this.nuevaPersona.idParentesco,
          'idParentesco',
          null,
          'El parentesco es obligatorio.'
        ) && valido;

      if (
        valido &&
        this.nuevaPersona.cuil &&
        this.nuevaPersona.cuilTitular &&
        this.nuevaPersona.idParentesco
      ) {
        const parentescoSeleccionado = this.parentescos.find(
          (p) => String(p.id) === String(this.nuevaPersona.idParentesco)
        );

        if (parentescoSeleccionado) {
          const esTitular = parentescoSeleccionado.descripcion
            .toUpperCase()
            .includes('TITULAR');
          const cuilIgualATitular =
            this.nuevaPersona.cuil === this.nuevaPersona.cuilTitular;

          if (cuilIgualATitular && !esTitular) {
            this.errores['idParentesco'] =
              'Cuando el CUIL y CUIL Titular son iguales, el parentesco debe ser TITULAR';
            valido = false;
          }

          if (!cuilIgualATitular && esTitular) {
            this.errores['idParentesco'] =
              'Cuando el CUIL y CUIL Titular son diferentes, el parentesco no puede ser TITULAR';
            valido = false;
          }
        }
      }

      if (valido) {
        Promise.all([this.verificarCuil(), this.verificarCuilTitular()])
          .then((results) => {
            resolve(results.every((result) => result));
          })
          .catch(() => {
            resolve(false);
          });
      } else {
        resolve(valido);
      }
    });
  }

  private validarCampo(
    valor: any,
    campo: string,
    regex: RegExp | null,
    mensajeVacio: string,
    mensajeInvalido?: string
  ): boolean {
    if (!valor) {
      this.errores[campo] = mensajeVacio;
      return false;
    }
    if (regex && !regex.test(valor)) {
      this.errores[campo] = mensajeInvalido || 'Formato inválido.';
      return false;
    }
    return true;
  }

  private verificarCuil(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.nuevaPersona.cuil) {
        this.apiSrv
          .verificarPersonaExistente(this.nuevaPersona.cuil)
          .subscribe({
            next: (existe: boolean) => {
              if (existe) {
                this.errores['cuil'] = 'Ya existe una persona con este CUIL.';
              }
              resolve(!existe);
            },
            error: () => {
              this.errores['cuil'] = 'Error al verificar el CUIL.';
              resolve(false);
            },
          });
      } else {
        resolve(true);
      }
    });
  }

  private verificarCuilTitular(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.nuevaPersona.cuil !== this.nuevaPersona.cuilTitular) {
        if (
          this.cuilTitularPrevioVerificado !== this.nuevaPersona.cuilTitular
        ) {
          this.reiniciarDatosAfiliacion();
        }

        this.apiSrv.obtenerPersona(this.nuevaPersona.cuilTitular).subscribe({
          next: (persona: any) => {
            if (!persona || !persona.cuil) {
              this.errores['cuilTitular'] =
                'El titular con el CUIL proporcionado no existe.';
              this.tieneTitular = false;
              resolve(false);
            } else {
              this.nuevaPersona.idPlan = persona?.plan?.id || '';
              this.nuevaPersona.idObraSocial = persona?.obraSocial?.id || '';
              this.nuevaPersona.idRevista = persona?.revista?.id || '';
              this.nuevaPersona.idTipoBeneficiario =
                persona?.tipoBeneficiario?.id || '';
              this.tieneTitular = true;
              this.nuevaPersona.planFijo = persona?.planFijo || false;

              const empresaPersona =
                persona?.empresa || persona?.empresaPrincipal;

              if (empresaPersona?.cuit) {
                const cuitStr = empresaPersona.cuit.toString();
                this.nuevaPersona.cuitEmpresa = cuitStr;

                // Encontrar y establecer la empresa seleccionada
                this.empresaSeleccionada = this.empresas.find(
                  (e) => e.cuit.toString() === cuitStr
                );

                // También actualizar el filtro para mostrar en el campo de búsqueda
                this.empresaFiltro = empresaPersona.razonSocial || cuitStr;
              } else {
                this.nuevaPersona.cuitEmpresa = null;
                this.empresaSeleccionada = null;
                this.empresaFiltro = '';
              }

              this.nuevaPersona.aporteInicial = persona?.aporteInicial || 0.0;

              // Ahora sí, actualizar el CUIL verificado
              this.cuilTitularPrevioVerificado = this.nuevaPersona.cuilTitular;
              resolve(true);
            }
          },
          error: () => {
            this.errores['cuilTitular'] =
              'Error al verificar el CUIL del titular.';
            resolve(false);
          },
        });
      } else {
        this.tieneTitular = false;
        resolve(true);
      }
    });
  }

  private validarPersonaSegundoCampo(): boolean {
    this.errores = {};
    let valido = true;

    valido =
      this.validarCampo(
        this.nuevaPersona.idTipoBeneficiario,
        'idTipoBeneficiario',
        null,
        'El tipo de beneficiario es obligatorio.'
      ) && valido;
    valido =
      this.validarCampo(
        this.nuevaPersona.idPlan,
        'idPlan',
        null,
        'El plan es obligatorio.'
      ) && valido;
    valido =
      this.validarCampo(
        this.nuevaPersona.idObraSocial,
        'idObraSocial',
        null,
        'La obra social es obligatoria.'
      ) && valido;
    valido =
      this.validarCampo(
        this.nuevaPersona.idRevista,
        'idRevista',
        null,
        'La revista es obligatoria.'
      ) && valido;

    // Validar empresa si es un titular
    if (!this.tieneTitular) {
      const parentescoSeleccionado = this.parentescos.find(
        (p) => String(p.id) === String(this.nuevaPersona.idParentesco)
      );

      if (
        parentescoSeleccionado?.descripcion?.toUpperCase().includes('TITULAR')
      ) {
        valido =
          this.validarCampo(
            this.nuevaPersona.cuitEmpresa,
            'cuitEmpresa',
            null,
            'Debe seleccionar una empresa.'
          ) && valido;
      }
    }

    if (this.tipoModal === 'venta') {
      valido =
        this.validarCampo(
          this.nuevaPersona.fechaVenta,
          'fechaVenta',
          null,
          'La fecha de venta es obligatoria.'
        ) && valido;

      valido =
        this.validarCampo(
          this.nuevaPersona.idVendedor,
          'idVendedor',
          null,
          'Debe seleccionar un vendedor.'
        ) && valido;
    }

    if (!this.tieneTitular) {
      valido =
        this.validarCampo(
          this.nuevaPersona.aporteInicial,
          'aporteInicial',
          null,
          'El monto de aporte es obligatorio.'
        ) && valido;

      // Validar que sea un número válido y positivo
      if (this.nuevaPersona.aporteInicial <= 0) {
        this.errores['aporteInicial'] = 'El monto debe ser mayor que cero';
        valido = false;
      }
    }

    return valido;
  }

  private resetForm(): void {
    this.nuevaPersona = this.getInitialPersona();
    this.localidades = [];
  }

  private getInitialPersona(): any {
    if (environment.development) {
      return {
        nombre: 'Juan',
        apellido: 'Pérez',
        documento: '30123456',
        idSexo: '1',
        idEstadoCivil: '1',
        idNacionalidad: '1',
        idParentesco: '1',
        idTipoBeneficiario: '1',
        idPlan: '1',
        idObraSocial: '1',
        idRevista: '1',
        telefono: '1123456789',
        cuil: '20301234560',
        cuilTitular: '20301234560',
        numeroAfiliado: '12345',
        domicilio: 'Calle Falsa',
        numeroDomicilio: '123',
        piso: '2',
        departamento: 'B',
        idLocalidad: '1',
        provincia: '2',
        idVendedor: '',
        aporteInicial: 0.0,
        empresaFiltro: '',
        planFijo: false,
      };
    }

    return {
      nombre: '',
      apellido: '',
      documento: '',
      fechaNacimiento: '',
      fechaVenta: '',
      idSexo: '',
      idEstadoCivil: '',
      idNacionalidad: '',
      idParentesco: '',
      idTipoBeneficiario: '',
      idPlan: '',
      idObraSocial: '',
      idRevista: '',
      telefono: '',
      cuil: '',
      cuilTitular: '',
      numeroAfiliado: '',
      domicilio: '',
      numeroDomicilio: '',
      piso: '',
      departamento: '',
      idLocalidad: '',
      provincia: '',
      idVendedor: '',
      aporteInicial: 0.0,
      empresaFiltro: '',
      planFijo: false,
    };
  }

  cerrarModal(): void {
    this.hideModal('nuevoPersonaModal');
    this.hideModal('opcionesAdherenteModal');
    this.personaComponent.eliminarModalBackdrop();
  }

  private hideModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      modal?.hide();
    }

    const backdrop = document.querySelector('.modal-backdrop');
    if (backdrop) {
      backdrop.parentNode?.removeChild(backdrop);
    }
  }

  guardarPersonaYAgregarAdherentes(): void {
    if (this.validarPersonaSegundoCampo()) {
      this.loadingSrv.setLoading(true);
      this.apiSrv.agregarPersona(this.nuevaPersona).subscribe({
        next: (response) => {
          this.loadingSrv.setLoading(false);
          if (response.status === 201) {
            this.toastSrv.mostrarNotificacion(
              'Persona guardada correctamente. Ahora puede agregar adherentes.',
              'success'
            );
            this.cuilTitularPrevio = this.nuevaPersona.cuilTitular;
            this.idPlanPrevio = this.nuevaPersona.idPlan;
            this.idObraSocialPrevio = this.nuevaPersona.idObraSocial;
            this.idRevistaPrevio = this.nuevaPersona.idRevista;
            this.idTipoBeneficiarioPrevio =
              this.nuevaPersona.idTipoBeneficiario;
            this.aportePrevio = this.nuevaPersona.aporteInicial || 0.0;
            this.cuitEmpresaPrevio = this.nuevaPersona.cuitEmpresa || '';
            this.telefonoPrevio = this.nuevaPersona.telefono || '';
            this.domicilioPrevio = this.nuevaPersona.domicilio || '';
            this.numeroDomicilioPrevio =
              this.nuevaPersona.numeroDomicilio || '';
            this.pisoPrevio = this.nuevaPersona.piso || '';
            this.departamentoPrevio = this.nuevaPersona.departamento || '';
            this.provinciaPrevio = this.nuevaPersona.provincia || '';
            this.idLocalidadPrevio = this.nuevaPersona.idLocalidad || '';
            this.idNacionalidadPrevio = this.nuevaPersona.idNacionalidad || '';
            this.fechaVentaPrevio = this.nuevaPersona.fechaVenta || '';
            this.idVendedorPrevio = this.nuevaPersona.idVendedor || '';
            const currentType = this.tipoModal;
            this.resetFormAdherente();
            this.tipoModal = currentType;
            this.seccionActual = 'nuevaPersonaAdherente';
          } else {
            this.toastSrv.mostrarNotificacion(
              'Error al guardar la persona.',
              'error'
            );
          }
        },
        error: this.onGuardarError.bind(this),
      });
    }
  }

  private onPersonaGuardada(response: any): void {
    this.loadingSrv.setLoading(false);
    if (response.status === 201) {
      this.toastSrv.mostrarNotificacion(
        'Persona guardada correctamente.',
        'success'
      );
      this.cuilTitularPrevio = this.nuevaPersona.cuilTitular;
      this.idPlanPrevio = this.nuevaPersona.idPlan;
      this.idObraSocialPrevio = this.nuevaPersona.idObraSocial;
      this.idRevistaPrevio = this.nuevaPersona.idRevista;
      this.idTipoBeneficiarioPrevio = this.nuevaPersona.idTipoBeneficiario;
      this.aportePrevio = this.nuevaPersona.aporteInicial || 0.0;
      this.cuitEmpresaPrevio = this.nuevaPersona.cuitEmpresa || '';
      this.planFijoPrevio = this.nuevaPersona.planFijo || false;

      this.telefonoPrevio = this.nuevaPersona.telefono || '';
      this.domicilioPrevio = this.nuevaPersona.domicilio || '';
      this.numeroDomicilioPrevio = this.nuevaPersona.numeroDomicilio || '';
      this.pisoPrevio = this.nuevaPersona.piso || '';
      this.departamentoPrevio = this.nuevaPersona.departamento || '';
      this.provinciaPrevio = this.nuevaPersona.provincia || '';
      this.idLocalidadPrevio = this.nuevaPersona.idLocalidad || '';
      this.idNacionalidadPrevio = this.nuevaPersona.idNacionalidad || '';
      this.fechaVentaPrevio = this.nuevaPersona.fechaVenta || '';
      this.idVendedorPrevio = this.nuevaPersona.idVendedor || '';

      if (this.seccionActual === 'nuevaPersonaAdherente') {
        this.hideModal('nuevoPersonaModal');
        this.resetForm();
        this.mostrarOpcionesAdherente();
      }

      if (this.seccionActual === 'datosAfiliacion') {
        this.resetForm();
        this.cerrarModal();
        this.seccionActual = 'datosPersonales';
      }

      this.personaComponent.eliminarModalBackdrop();
      this.personaComponent.recargarPersonas();
    } else {
      this.toastSrv.mostrarNotificacion(
        'Error al guardar la persona.',
        'error'
      );
    }
  }

  mostrarOpcionesAdherente(): void {
    const modalElement = document.getElementById('opcionesAdherenteModal');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  private resetFormAdherente(): void {
    this.nuevaPersona = {
      ...this.getInitialPersona(),
      idTipoBeneficiario: this.idTipoBeneficiarioPrevio,
      idPlan: this.idPlanPrevio,
      idObraSocial: this.idObraSocialPrevio,
      idRevista: this.idRevistaPrevio,
      cuilTitular: this.cuilTitularPrevio,
      aporteInicial: this.aportePrevio,
      cuitEmpresa: this.cuitEmpresaPrevio,
      planFijo: this.planFijoPrevio,
      // Autocompletar datos del titular
      telefono: this.telefonoPrevio,
      domicilio: this.domicilioPrevio,
      numeroDomicilio: this.numeroDomicilioPrevio,
      piso: this.pisoPrevio,
      departamento: this.departamentoPrevio,
      idLocalidad: this.idLocalidadPrevio,
      provincia: this.provinciaPrevio,
      idNacionalidad: this.idNacionalidadPrevio,
      fechaVenta: this.fechaVentaPrevio,
      idVendedor: this.idVendedorPrevio,
    };

    if (this.cuitEmpresaPrevio) {
      this.empresaSeleccionada = this.empresas.find(
        (e) => e.cuit.toString() === this.cuitEmpresaPrevio.toString()
      );
    } else {
      this.empresaSeleccionada = null;
    }
    this.cargarLocalidadesYSeleccionar(
      this.provinciaPrevio,
      this.idLocalidadPrevio
    );
  }

  abrirModalNuevoAdherente(): void {
    this.resetFormAdherente();
    this.hideModal('opcionesAdherenteModal');
    this.showModal('nuevoPersonaModal');
  }

  private showModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      modal?.show();
    }
  }

  agregarAdherenteModal(
    personaSeleccionada: any,
    tipo: 'venta' | 'persona' = 'persona'
  ): void {
    this.personaSeleccionada = personaSeleccionada;
    this.nuevaPersona.cuilTitular = this.personaSeleccionada.cuilTitular;
    this.nuevaPersona.idPlan = this.personaSeleccionada.plan.id;
    this.nuevaPersona.idObraSocial = this.personaSeleccionada.obraSocial.id;
    this.nuevaPersona.idRevista = this.personaSeleccionada.revista.id;
    this.nuevaPersona.idTipoBeneficiario =
      this.personaSeleccionada.tipoBeneficiario.id;
    this.nuevaPersona.aporteInicial = this.personaSeleccionada.aporteInicial;
    this.nuevaPersona.planFijo = this.personaSeleccionada.planFijo || false;
    this.nuevaPersona.telefono = this.personaSeleccionada.telefono || '';
    this.nuevaPersona.domicilio =
      this.personaSeleccionada.nombreDomicilio ||
      this.personaSeleccionada.domicilio ||
      '';
    this.nuevaPersona.numeroDomicilio =
      this.personaSeleccionada.numeroDomicilio || '';
    this.nuevaPersona.piso = this.personaSeleccionada.piso || '';
    this.nuevaPersona.departamento =
      this.personaSeleccionada.departamento || '';
    this.nuevaPersona.idLocalidad =
      this.personaSeleccionada.localidad?.id || '';
    this.nuevaPersona.provincia =
      this.personaSeleccionada.localidad?.provincia?.id || '';
    this.nuevaPersona.idNacionalidad =
      this.personaSeleccionada.nacionalidad?.id || '';
    this.nuevaPersona.fechaVenta = this.personaSeleccionada.fechaVenta || '';
    this.nuevaPersona.idVendedor = this.personaSeleccionada.vendedor?.id || '';

    if (this.personaSeleccionada.empresa?.cuit) {
      const cuitStr = this.personaSeleccionada.empresa.cuit.toString();
      this.nuevaPersona.cuitEmpresa = cuitStr;
      this.empresaSeleccionada = this.empresas.find(
        (e) => e.cuit.toString() === cuitStr
      );
    } else if (this.personaSeleccionada.cuitEmpresa) {
      const cuitStr = this.personaSeleccionada.cuitEmpresa.toString();
      this.nuevaPersona.cuitEmpresa = cuitStr;
      this.empresaSeleccionada = this.empresas.find(
        (e) => e.cuit.toString() === cuitStr
      );
    } else {
      this.nuevaPersona.cuitEmpresa = null;
      this.empresaSeleccionada = null;
    }

    this.tipoModal = tipo;
    this.cargarLocalidadesYSeleccionar(
      this.nuevaPersona.provincia,
      this.nuevaPersona.idLocalidad
    );
    this.seccionActual = 'nuevaPersonaAdherente';
    this.showModal('nuevoPersonaModal');
  }

  limpiarModalAgregarPersona(tipo: 'persona' | 'venta' = 'persona'): void {
    this.tipoModal = tipo;
    this.resetForm();
    this.seccionActual = 'datosPersonales';
  }

  abrirModalVendedor(): void {
    this.modalPersonaVisible = true;
    this.hideModal('nuevoPersonaModal');
    setTimeout(() => {
      if (this.vendedorModal) {
        this.vendedorModal.abrirModoCreacionVendedor();
      }
    }, 300);
  }

  abrirModalEmpresa(): void {
    this.modalPersonaVisible = true;
    this.hideModal('nuevoPersonaModal');

    setTimeout(() => {
      if (this.empresaModal) {
        this.empresaModal.abrirModoCreacionEmpresa();

        if (!this.empresaModalSuscrito) {
          this.empresaModalSuscrito = true;

          this.empresaModal.empresaGuardada
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              this.cargarEmpresas();
              if (this.modalPersonaVisible) {
                setTimeout(() => {
                  this.showModal('nuevoPersonaModal');
                  this.modalPersonaVisible = false;
                }, 500);
              }
            });

          this.empresaModal.empresaModalCancelado
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              if (this.modalPersonaVisible) {
                setTimeout(() => {
                  this.showModal('nuevoPersonaModal');
                  this.modalPersonaVisible = false;
                }, 300);
              }
            });
        }
      }
    }, 300);
  }

  private reiniciarDatosAfiliacion(): void {
    this.tieneTitular = false;
    this.nuevaPersona.idPlan = '';
    this.nuevaPersona.idObraSocial = '';
    this.nuevaPersona.idRevista = '';
    this.nuevaPersona.idTipoBeneficiario = '';
    this.nuevaPersona.aporteInicial = 0.0;
    this.nuevaPersona.cuitEmpresa = null;
    this.nuevaPersona.numeroAfiliado = '';
    this.nuevaPersona.planFijo = false;
    this.empresaSeleccionada = null;
    this.empresaFiltro = '';
    // Reset el CUIL del titular verificado para forzar una nueva verificación
    this.cuilTitularPrevioVerificado = '';
  }

  onCuilTitularChange(): void {
    // Indica que el CUIL del titular ha cambiado y se deben limpiar los datos en la próxima transición
    this.cuilTitularPrevioVerificado = '';

    // Si estamos en la pestaña de datos de afiliación, reiniciar inmediatamente
    if (this.seccionActual === 'datosAfiliacion') {
      this.reiniciarDatosAfiliacion();

      // Verificar el nuevo CUIL si estamos en datosAfiliacion
      if (this.nuevaPersona.cuil !== this.nuevaPersona.cuilTitular) {
        this.verificarCuilTitular();
      }
    }
  }

  onFechaInput(event: Event, datepicker: any): void {
    const input = event.target as HTMLInputElement;
    const fieldId = (input as any).id || '';
    const errorKey =
      fieldId === 'fechaVenta' ? 'fechaVenta' : 'fechaNacimiento';

    // Guardar posición del cursor
    const cursorPos = input.selectionStart || 0;

    let valor = (input.value || '').replace(/[^\d/]/g, '');

    // Detectar si el usuario está escribiendo barras manuales en posiciones NO estándar
    // Barras en posiciones 2 y 5 son auto-insertadas, otras son manuales
    const barrasEnPosiciones = [];
    for (let i = 0; i < valor.length; i++) {
      if (valor[i] === '/') {
        barrasEnPosiciones.push(i);
      }
    }

    // Verificar si hay barras en posiciones que NO son 2 o 5
    const tieneBarrasUsuario = barrasEnPosiciones.some(
      (pos) => pos !== 2 && pos !== 5
    );

    if (!tieneBarrasUsuario) {
      // Si no hay barras manuales, aplicar auto-formato
      const digitos = valor.replace(/\//g, '');

      let formatted = '';
      for (let i = 0; i < digitos.length && i < 8; i++) {
        // Insertar barra antes del tercer dígito (índice 2)
        if (i === 2) {
          formatted += '/';
        }
        // Insertar barra antes del quinto dígito (índice 4)
        if (i === 4) {
          formatted += '/';
        }
        formatted += digitos[i];
      }

      input.value = formatted;

      // Ajustar cursor
      let newCursorPos = cursorPos;
      if (formatted.length > cursorPos) {
        const barrasAntes = (
          formatted.substring(0, cursorPos).match(/\//g) || []
        ).length;
        newCursorPos = cursorPos + barrasAntes;
      }
      input.setSelectionRange(newCursorPos, newCursorPos);
    } else {
      // Si hay barras manuales, solo limpiar y permitir
      input.value = valor;
      input.setSelectionRange(cursorPos, cursorPos);
    }

    const partesFinales = input.value.split('/');
    if (
      partesFinales.length === 3 &&
      partesFinales[2].length === 4 &&
      partesFinales[0] &&
      partesFinales[1]
    ) {
      const dia = partesFinales[0].padStart(2, '0');
      const mes = partesFinales[1].padStart(2, '0');
      const año = partesFinales[2];

      const fechaFormateada = `${dia}/${mes}/${año}`;
      input.value = fechaFormateada;

      const diaNum = parseInt(dia, 10);
      const mesNum = parseInt(mes, 10);
      const añoNum = parseInt(año, 10);

      if (
        diaNum >= 1 &&
        diaNum <= 31 &&
        mesNum >= 1 &&
        mesNum <= 12 &&
        añoNum >= 1900 &&
        añoNum <= this.maxDate.year
      ) {
        const fechaValida = this.validarFecha(diaNum, mesNum, añoNum);
        const fechaIngresada = new Date(añoNum, mesNum - 1, diaNum);
        const fechaActual = new Date();
        const esFechaFutura = fechaIngresada > fechaActual;

        if (fechaValida && !esFechaFutura) {
          try {
            datepicker.navigateTo({ year: añoNum, month: mesNum, day: diaNum });
          } catch (error) {
            // Ignorar errores del datepicker
          }
          delete this.errores[errorKey];
        } else if (esFechaFutura) {
          const mensaje =
            errorKey === 'fechaVenta'
              ? 'La fecha de venta no puede ser futura'
              : 'La fecha de nacimiento no puede ser futura';
          this.errores[errorKey] = mensaje;
          if (errorKey === 'fechaNacimiento') {
            this.toastSrv.mostrarNotificacion(mensaje, 'error');
          }
        } else {
          const mensaje =
            errorKey === 'fechaVenta'
              ? 'La fecha de venta ingresada no es válida'
              : 'La fecha ingresada no es válida';
          this.errores[errorKey] = mensaje;
          if (errorKey === 'fechaNacimiento') {
            this.toastSrv.mostrarNotificacion(mensaje, 'error');
          }
        }
      } else {
        const mensaje =
          errorKey === 'fechaVenta'
            ? 'La fecha de venta ingresada no es válida'
            : 'La fecha ingresada no es válida';
        this.errores[errorKey] = mensaje;
      }
    } else {
      delete this.errores[errorKey];
    }
  }

  private validarFecha(dia: number, mes: number, año: number): boolean {
    // Validar que la fecha sea válida (considerando días del mes)
    const fecha = new Date(año, mes - 1, dia);
    return (
      fecha.getFullYear() === año &&
      fecha.getMonth() === mes - 1 &&
      fecha.getDate() === dia
    );
  }

  private validarFechaCompleta(fechaStr: string): {
    valido: boolean;
    mensaje?: string;
  } {
    // Validar que no esté vacío
    if (!fechaStr || fechaStr.trim() === '') {
      return {
        valido: false,
        mensaje: 'La fecha de nacimiento es obligatoria.',
      };
    }

    // Validar formato DD/MM/YYYY
    if (fechaStr.length !== 10) {
      return {
        valido: false,
        mensaje: 'La fecha debe tener el formato DD/MM/YYYY.',
      };
    }

    const partes = fechaStr.split('/');
    if (partes.length !== 3) {
      return {
        valido: false,
        mensaje: 'La fecha debe tener el formato DD/MM/YYYY.',
      };
    }

    const dia = parseInt(partes[0], 10);
    const mes = parseInt(partes[1], 10);
    const año = parseInt(partes[2], 10);

    // Validar rangos básicos
    if (isNaN(dia) || isNaN(mes) || isNaN(año)) {
      return {
        valido: false,
        mensaje: 'La fecha contiene valores no numéricos.',
      };
    }

    if (dia < 1 || dia > 31) {
      return { valido: false, mensaje: 'El día debe estar entre 1 y 31.' };
    }

    if (mes < 1 || mes > 12) {
      return { valido: false, mensaje: 'El mes debe estar entre 1 y 12.' };
    }

    if (año < 1900 || año > this.maxDate.year) {
      return {
        valido: false,
        mensaje: `El año debe estar entre 1900 y ${this.maxDate.year}.`,
      };
    }

    // Validar que la fecha sea real (considerando días del mes, años bisiestos, etc.)
    if (!this.validarFecha(dia, mes, año)) {
      return { valido: false, mensaje: 'La fecha ingresada no es válida.' };
    }

    // Validar que no sea una fecha futura
    const fechaIngresada = new Date(año, mes - 1, dia);
    const fechaActual = new Date();
    fechaActual.setHours(0, 0, 0, 0); // Resetear horas para comparar solo fechas

    if (fechaIngresada > fechaActual) {
      return {
        valido: false,
        mensaje: 'La fecha de nacimiento no puede ser futura.',
      };
    }

    return { valido: true };
  }

  private cargarLocalidadesYSeleccionar(
    provinciaId: any,
    idLocalidad?: any
  ): void {
    if (!provinciaId) {
      this.localidades = [];
      return;
    }
    this.apiSrv
      .obtenerLocalidadesSegunIdProvincia(Number(provinciaId))
      .subscribe({
        next: (localidades: any[]) => {
          this.localidades = localidades || [];
          const idLocStr = (
            idLocalidad ??
            this.nuevaPersona.idLocalidad ??
            ''
          ).toString();
          const existe = this.localidades.some(
            (l) => l.id?.toString() === idLocStr
          );
          this.nuevaPersona.idLocalidad = existe ? idLocStr : '';
        },
        error: () => {
          this.localidades = [];
          this.nuevaPersona.idLocalidad = '';
        },
      });
  }
}
